import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSiteSettings } from "@/hooks/useSiteData";
import { useLanguage, t } from "@/contexts/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import logo from "@/assets/logo-painur-small.avif";

const linkDefs = [
  { to: "/clinic", key: "home" as const },
  { to: "/services", key: "services" as const },
  { to: "/team", key: "team" as const },
  { to: "/articles", key: "articles" as const },
  { to: "/about", key: "about" as const },
  { to: "/contact", key: "contact" as const },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { lang } = useLanguage();
  const CLINIC = useSiteSettings();
  const links = linkDefs.map((l) => ({ to: l.to, label: t[l.key][lang] }));
  const isLandingPage = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={cn(
        "fixed top-0 right-0 left-0 z-50 transition-all duration-500",
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-border/50 py-3"
          : "bg-transparent py-5"
      )}
    >
      <nav className="container mx-auto flex items-center justify-between px-4">
        <Link to="/clinic" className="flex items-center gap-3 group">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 blur-xl group-hover:bg-primary/40 transition-all" />
            <img
              src={logo}
              alt="בית הרופאים באקה - מרפאת כאב ושיקום"
              width={isLandingPage ? 112 : 80}
              height={isLandingPage ? 112 : 80}
              className={cn("relative w-auto", isLandingPage ? "h-20 sm:h-28" : "h-16 sm:h-20")}
            />
          </div>
          {!isLandingPage && (
            <div className="hidden sm:block">
              <div className="text-lg font-bold text-gradient leading-none">{CLINIC.name}</div>
              <div className="text-[10px] text-muted-foreground tracking-widest uppercase">{CLINIC.tagline}</div>
            </div>
          )}
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => {
            const active =
              l.to === "/"
                ? location.pathname === "/"
                : location.pathname === l.to || location.pathname.startsWith(l.to + "/");
            return (
              <Link
                key={l.to}
                to={l.to}
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-lg transition-all relative",
                  active
                    ? "text-primary"
                    : "text-foreground/80 hover:text-primary"
                )}
              >
                {l.label}
                {active && (
                  <span className="absolute bottom-0 right-1/2 translate-x-1/2 h-px w-8 bg-gradient-to-r from-transparent via-primary to-transparent" />
                )}
              </Link>
            );
          })}
          <Button asChild variant="outline" className="mr-2">
            <Link to="/">קבוצת פאניק</Link>
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher className="hidden sm:inline-flex" />
          <a
            href={`tel:${CLINIC.phone}`}
            className="hidden xl:inline-flex items-center gap-1.5 text-sm text-foreground/80 hover:text-primary transition-colors"
            title="טלפון מרפאה"
          >
            <Phone className="h-4 w-4" />
            <span dir="ltr">{CLINIC.phone}</span>
          </a>
          <Button
            asChild
            className="hidden sm:inline-flex bg-gradient-to-r from-primary to-secondary text-primary-foreground hover:opacity-90 hover:shadow-[0_0_30px_hsl(var(--primary)/0.5)] transition-all"
          >
            <Link to="/contact">{t.bookAppointment[lang]}</Link>
          </Button>
          <a
            href={`tel:${CLINIC.phone}`}
            aria-label={`התקשר ${CLINIC.phone}`}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 border border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-all"
          >
            <Phone className="h-4 w-4" />
          </a>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-foreground"
            aria-label={t.menu[lang]}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-background/95 backdrop-blur-xl border-b border-border/50 animate-fade-in">
          <div className="container mx-auto px-4 py-6 flex flex-col gap-2">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={cn(
                  "px-4 py-3 rounded-lg text-base font-medium transition-colors",
                  location.pathname === l.to
                    ? "bg-primary/10 text-primary"
                    : "text-foreground/80 hover:bg-muted hover:text-primary"
                )}
              >
                {l.label}
              </Link>
            ))}
            <div className="mt-3 flex items-center gap-2">
              <Button asChild variant="outline" className="flex-1">
                <Link to="/">קבוצת פאניק</Link>
              </Button>
              <LanguageSwitcher className="flex-1 justify-center" />
              <Button
                asChild
                className="flex-1 bg-gradient-to-r from-primary to-secondary text-primary-foreground"
              >
                <Link to="/contact">{t.bookAppointment[lang]}</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
